import { IonButton, IonChip, IonIcon, IonLabel } from '@ionic/react';
import {
  FileTextIcon,
  IconProps,
  PencilSimpleIcon,
  PlusCircleIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { refreshOutline } from 'ionicons/icons';

import { formatMiles } from '../../../utils/planes';
import {
  acortarNombreVet,
  destacarVetName,
  getIndicacionMensaje,
} from '../../../utils/reintegro';
import {
  formatTipo,
  getColorByEstado,
  getComprobantesStats,
  getDescripcion,
} from '../../../utils/siniestro';
import { capitalizeEveryWord, capitalizeFirstLetter } from '../../../utils/string';
import { getVeterinarioNombreCompleto } from '../../../utils/veterinario';
import Button from '../../UI/Button';
import Skeleton from '../../UI/Skeleton';
import Spinner from '../../UI/Spinner';
import './ReintegroCard.scss';
import { ReintegroCardProps } from './types';

const iconProps: IconProps = { size: 24, weight: 'fill' };

const ReintegroCard: React.FC<ReintegroCardProps> = ({
  svf,
  comprobantes = [],
  documentacionCompleta,
  isVacuna,
  isLoadingComprobantes,
  openComprobantesGallery,
  onEditVeterinario,
  onEditDocumentacion,
  onAddComprobante,
  onResendVeterinario,
  resendingVetFormId,
}) => {
  const estado = svf.vetForm?.estado ?? 'ENVIADO';
  const veterinario = getVeterinarioNombreCompleto(svf.vet);
  const { cantidad, totalMonto } = getComprobantesStats(comprobantes);
  const isResendLoading = resendingVetFormId === svf.vetFormId;

  return (
    <div className="reintegro-card">
      <div className="reintegro-card__header">
        <h3 className="reintegro-card__title">{formatTipo(svf.tipo)}</h3>
        {/* Botón de reenviar al veterinario (solo si no es vacuna y estado es ENVIADO) */}
        {!isVacuna && estado === 'ENVIADO' && (
          <div className="reintegro-card__resend-container">
            {isResendLoading ? (
              <Spinner color="light" />
            ) : (
              <IonButton
                className="reintegro-card__resend-badge"
                onClick={() => onResendVeterinario(svf)}
                fill="clear"
                size="small"
                mode="ios"
              >
                Reenviar
                <IonIcon icon={refreshOutline} slot="end" />
              </IonButton>
            )}
          </div>
        )}
      </div>

      {/* Mensaje de indicación (solo si no es vacuna) */}
      {!isVacuna && (
        <p className="reintegro-card__message">
          {destacarVetName(getIndicacionMensaje(estado, svf.vet?.nombre))}
        </p>
      )}

      <div className="reintegro-card__content">
        <div className="reintegro-card__row">
          <span className="reintegro-card__label">Estado</span>
          <IonChip color={getColorByEstado(estado)} className="reintegro-card__chip">
            <IonLabel>{capitalizeFirstLetter(estado)}</IonLabel>
          </IonChip>
        </div>

        <div
          className="reintegro-card__row"
          hidden={estado === 'ENVIADO' || getDescripcion(svf) === 'Sin detalles'}
        >
          <span className="reintegro-card__label">Descripción</span>
          <span className="reintegro-card__value">{getDescripcion(svf)}</span>
        </div>

        {/* Documentación */}
        <div className="reintegro-card__row">
          <span className="reintegro-card__label">Documentación</span>
          <div className="reintegro-card__row-actions">
            {documentacionCompleta ? (
              <IonChip color="success" className="reintegro-card__chip">
                <IonLabel>Completada</IonLabel>
              </IonChip>
            ) : (
              <IonChip color="danger" className="reintegro-card__chip">
                <IonLabel>Incompleta</IonLabel>
              </IonChip>
            )}
            {!documentacionCompleta && onEditDocumentacion && (
              <button onClick={onEditDocumentacion} className="reintegro-card__icon-btn">
                <FileTextIcon {...iconProps} />
              </button>
            )}
          </div>
        </div>

        {/* Veterinario (solo si no es vacuna) */}
        {!isVacuna && (
          <div className="reintegro-card__row">
            <span className="reintegro-card__label">Veterinario</span>
            <div className="reintegro-card__row-actions">
              <span className="reintegro-card__value">
                {capitalizeEveryWord(acortarNombreVet(veterinario)) || '—'}
              </span>
              {onEditVeterinario && (
                <button
                  onClick={() => onEditVeterinario(svf)}
                  className="reintegro-card__icon-btn"
                >
                  <PencilSimpleIcon {...iconProps} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Comprobantes */}
        <div className="reintegro-card__row">
          <span className="reintegro-card__label">Comprobantes</span>
          <div className="reintegro-card__row-actions">
            {isLoadingComprobantes ? (
              <Spinner />
            ) : (
              <>
                <span className="reintegro-card__value">{cantidad || 'Ninguno'}</span>
                {onAddComprobante && (
                  <button
                    onClick={() => onAddComprobante(svf.vetFormId)}
                    className="reintegro-card__icon-btn"
                  >
                    <PlusCircleIcon {...iconProps} />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {isLoadingComprobantes ? (
          <div className="ion-padding-horizontal">
            <Skeleton height="55px" borderRadius="12px" style={{ marginTop: '16px', marginBottom: '16px' }} />
          </div>
        ) : (
          cantidad > 0 && (
            <>
              <div className="reintegro-card__row">
                <span className="reintegro-card__label">Costo total</span>
                <span className="reintegro-card__value">
                  ${formatMiles(String(totalMonto))}
                </span>
              </div>

              <div className="ion-padding-horizontal" style={{ paddingBottom: 20 }}>
                <Button
                  noMarginTop
                  marginBottom={false}
                  fill="outline"
                  size="small"
                  onClick={() => openComprobantesGallery(svf.id)}
                >
                  Ver comprobantes
                </Button>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default React.memo(ReintegroCard);
